import { ReactNode, createContext, useContext } from 'react'

import { BlankSlate, Box, Dialog, Icon, Spinner } from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { ActivityComponent, SerializableProps } from './types'

type ActivityProps = {
  children: ReactNode
  loading?: boolean
  error?: unknown
} & (
  | {
      loading: boolean
      error: unknown
    }
  | {
      loading?: never
      error?: never
    }
)

export type RenderedActivity<Data extends SerializableProps = Record<string, string>> = {
  component: ActivityComponent<Data>
  data: Data
}

const ActivityContext = createContext<{
  open: boolean
  closeActivity: () => void
} | null>(null)

export const useActivity = () => {
  const context = useContext(ActivityContext)
  if (!context) {
    throw new Error('useActivity must be used within a ActivityProvider')
  }
  return context
}

export const ActivityContainer: React.FC<{
  activity: RenderedActivity | null
  closeActivity: () => void
}> = ({ activity, closeActivity }) => {
  const value = useUnsafeMemo(() => {
    return {
      open: Boolean(activity),
      closeActivity,
    }
  }, [activity, closeActivity])

  return (
    <ActivityContext.Provider value={value}>
      <AnimatePresence>
        {activity && (
          <Box.Motion
            // This is a trick to delay closing the modal
            // so the it can actually animate out instead
            // of abruptly disappearing.
            // Framer motion keeps the element in the DOM
            // and the animation happens in the modal
            // component.
            initial={{ opacity: 0.99 }}
            exit={{ opacity: 0 }}
            animate={{ opacity: 0.99 }}
            transition={{ duration: 500 }}
          >
            {activity.component && <activity.component {...activity.data} />}
          </Box.Motion>
        )}
      </AnimatePresence>
    </ActivityContext.Provider>
  )
}

export const Activity: React.FC<ActivityProps> = ({ error, loading, children }) => {
  const { open, closeActivity } = useActivity()

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && closeActivity()}>
      <Dialog.Content>
        {Boolean(loading) && <Spinner />}
        {Boolean(error) && <BlankSlate illustration="alert" title="" description="" />}

        {!loading && !error && children}
        <Dialog.Close>
          <Box>
            <Icon.Medium icon="cross" />
          </Box>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  )
}
